<script setup lang="ts">
import { Comment, computed, onBeforeUnmount, ref, useSlots, watch } from 'vue'
import { arrow as arrowPlugin, autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/vue'
import type { Placement } from '@floating-ui/vue'
import { useThrottleFn, useTimeoutFn } from '@vueuse/core'
import type { ComplexClassName } from '../../types/common'
import { classArray } from '../../utils/class'
import { DEFAULT_DELAY_MS, TooltipTriggers, type TooltipVariant } from './types'

interface Props {
  title?: string
  content?: string
  placement?: Placement
  variant?: keyof typeof TooltipVariant
  tooltipClass?: ComplexClassName
  hideArrow?: boolean
  trigger?: keyof typeof TooltipTriggers | Array<Exclude<keyof typeof TooltipTriggers, 'manual'>>
  target?: HTMLElement
  show?: boolean
  delay?: number
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'top',
  variant: 'light',
  hideArrow: false,
  trigger: TooltipTriggers.hover,
  delay: DEFAULT_DELAY_MS,
})

const inlineTarget = ref()
const target = computed(() => props.target ?? inlineTarget.value as HTMLElement)
const tooltip = ref()
const arrow = ref()
const isHidden = ref<boolean>(true)

watch(() => props.show, () => {
  isHidden.value = !props.show
}, { immediate: true })

let cleanAutoUpdate: () => void | undefined
watch(isHidden, (isHidden) => {
  if (!isHidden && target.value)
    cleanAutoUpdate = autoUpdate(target.value, tooltip.value, calculatePosition)
  else
    cleanAutoUpdate?.()
}, { immediate: true })

onBeforeUnmount(() => {
  cleanAutoUpdate?.()
})

const tooltipClass = computed((): string[] => {
  const cls = [
    'w-max',
    'max-w-xs',
    'px-2.5',
    'py-2',
    'absolute',
    'top-0',
    'left-0',
    'z-50',
    'text-xs',
    'font-normal',
    'leading-4',
    'shadow-sm',
    'rounded-md',
  ]

  if (props.variant === 'light')
    cls.push('text-gray-500 bg-white border border-gray-200')
  else
    cls.push('text-gray-200 bg-black')

  if (isHidden.value)
    cls.push('hidden')

  if (props.tooltipClass)
    cls.push(...classArray(props.tooltipClass))

  return cls
})

const titleClass = computed((): string[] => {
  const cls = [
    'mb-1',
    'font-normal',
  ]

  if (props.variant === 'light')
    cls.push('text-gray-800')

  else
    cls.push('text-white')

  return cls
})

const arrowClass = computed((): string[] => {
  const cls = [
    'absolute',
    'h-2',
    'w-2',
  ]

  if (props.variant === 'light')
    cls.push('text-gray-500 bg-white border-t border-l border-gray-200')

  else
    cls.push('text-white bg-black border-t border-l border-black')

  if (props.hideArrow)
    cls.push('hidden')

  return cls
})

async function calculatePosition() {
  const { x, y, middlewareData, placement } = await computePosition(
    target.value,
    tooltip.value,
    {
      placement: props.placement,
      middleware: [
        offset(12),
        flip(),
        shift({ padding: 4 }),
        arrowPlugin({ element: arrow.value, padding: 6 }),
        {
          name: 'adjustTooltipZIndex',
          fn: ({ elements }) => {
            const getOffsetParentsZIndex = (element: Element): string | undefined => {
              if (element.nodeName === 'BODY')
                return undefined

              const parent = (element as HTMLElement)?.offsetParent
              if (!parent)
                return undefined

              const zIndex = getComputedStyle(parent).zIndex
              if (!zIndex || Number.isNaN(Number(zIndex)))
                return getOffsetParentsZIndex(parent)

              return zIndex
            }

            const zIndex = getOffsetParentsZIndex(elements.reference as Element)
            if (zIndex)
              tooltip.value!.style.zIndex = zIndex

            return {}
          },
        },
      ],
    },
  )

  Object.assign(tooltip.value.style, {
    transform: `translate3D(${Math.round(x)}px,${Math.round(y)}px, 0)`,
  })

  const { x: arrowX, y: arrowY } = middlewareData.arrow || { x: 0, y: 0 }

  const opposedSide = {
    left: 'right',
    right: 'left',
    bottom: 'top',
    top: 'bottom',
  }[placement.split('-')[0]]

  const arrowRotation = {
    left: 90,
    right: -90,
    bottom: 0,
    top: 180,
  }[placement.split('-')[0]] as number

  Object.assign(arrow.value.style, {
    left: arrowX != null ? `${arrowX}px` : '',
    top: arrowY != null ? `${arrowY}px` : '',
    bottom: '',
    right: '',
    transform: `rotate(${arrowRotation + 45}deg)`,
    [opposedSide as string]: '-4px',
  })
}

const pointerOverContent = ref(false)
const { start: startHideTimer, stop: stopHideTimer, isPending: hideTimerIsPending } = useTimeoutFn(() => {
  if (pointerOverContent.value)
    return

  isHidden.value = true
}, 150, { immediate: false })

const { start: startDisplayTimer, stop: stopDisplayTimer, isPending: displayTimerIsPending } = useTimeoutFn(() => {
  isHidden.value = false
}, props.delay, { immediate: false })

const stopCurrentTimers = () => {
  if (displayTimerIsPending.value)
    stopDisplayTimer()
  if (hideTimerIsPending.value)
    stopHideTimer()
}

function onMouseLeave() {
  if (!props.trigger.includes('hover'))
    return

  stopCurrentTimers()
  pointerOverContent.value = false
  startHideTimer()
}

function onMouseEnter() {
  stopCurrentTimers()
  startDisplayTimer()
}

const onMouseOver = useThrottleFn(() => {
  pointerOverContent.value = true
}, 50)

interface Triggers {
  click?: () => void
  mouseenter?: () => void
  mouseleave?: () => void
  focusin?: () => void
  focusout?: () => void
}

const triggers = computed<Triggers>(() => {
  const triggers = !Array.isArray(props.trigger) ? [props.trigger] : props.trigger

  return triggers.reduce((activeTriggers, trigger) => {
    switch (trigger) {
      case 'click':
        activeTriggers.click = () => isHidden.value = !isHidden.value
        break
      case 'hover':
        activeTriggers.mouseenter = onMouseEnter
        activeTriggers.mouseleave = onMouseLeave
        break
      case 'focus':
        activeTriggers.focusin = () => isHidden.value = false
        activeTriggers.focusout = () => isHidden.value = true
        break
    }
    return activeTriggers
  }, {} as Triggers)
})
defineExpose({
  triggers,
})

const slotIsEmpty = computed(() => {
  const slot = useSlots()?.default?.({})
  return !slot || (Array.isArray(slot) ? slot : []).every(vnode => vnode.type === Comment)
})
</script>

<template>
  <div
    class="inline-flex items-center"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave"
  >
    <div
      v-show="!slotIsEmpty"
      ref="inlineTarget"
      class="inline-block"
      :class="{ 'cursor-pointer': 'click' in triggers }"
      v-on="triggers"
    >
      <slot />
    </div>
    <Teleport to="body">
      <div
        ref="tooltip"
        :class="tooltipClass"
      >
        <slot name="content">
          <h6
            v-if="props.title"
            :class="titleClass"
          >
            {{ props.title }}
          </h6>
          <p v-if="props.content">
            {{ props.content }}
          </p>
        </slot>
        <div
          ref="arrow"
          :class="arrowClass"
        />
      </div>
    </Teleport>
  </div>
</template>
